import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import dummyUser from "../../../../assets/images/dummy/dummy-user.png";
import MassengerModals from "../../../../components/Navbar/MassengerModal";
import "./LinkedInChatDock.css";

const resolveComponent = (comp, fallback = "span") => {
  if (!comp) return fallback;
  if (comp.default && (typeof comp.default === "function" || typeof comp.default === "object")) {
    return comp.default;
  }
  return comp;
};

const CloseComp = resolveComponent(CloseIcon, () => null);
const ChevronDownComp = resolveComponent(KeyboardArrowDownIcon, () => null);
const ChevronUpComp = resolveComponent(KeyboardArrowUpIcon, () => null);
const OpenInNewComp = resolveComponent(OpenInNewIcon, () => null);
const ChatBubbleComp = resolveComponent(ChatBubbleOutlineIcon, () => null);

const LinkedInChatDock = ({
  isOpen = false,
  onClose,
  isMinimized = false,
  onToggleMinimize,
  initialProfile,
  userName = "User",
  userAvatar,
  unreadCount = 0,
  onOpenFullPage,
  onClearUnread,
}) => {
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    const handleMessagesRead = () => {
      if (typeof onClearUnread === "function") {
        onClearUnread();
      }
    };
    window.addEventListener("messages_read", handleMessagesRead);
    window.addEventListener("selectezi:messages_read", handleMessagesRead);
    return () => {
      window.removeEventListener("messages_read", handleMessagesRead);
      window.removeEventListener("selectezi:messages_read", handleMessagesRead);
    };
  }, [onClearUnread]);

  // When dock is open and expanded, clear unread count automatically
  useEffect(() => {
    if (
      isOpen &&
      !isMinimized &&
      unreadCount > 0 &&
      typeof onClearUnread === "function"
    ) {
      onClearUnread();
    }
  }, [isOpen, isMinimized, unreadCount, onClearUnread]);

  if (!isOpen) return null;

  const handleToggleMaximize = (e) => {
    e.stopPropagation();
    setIsMaximized((prev) => !prev);
    if (isMinimized && typeof onToggleMinimize === "function") {
      onToggleMinimize();
    }
    if (isMinimized && typeof onClearUnread === "function") {
      onClearUnread();
    }
  };

  const handleToggleMinimize = (e) => {
    if (e && typeof e.stopPropagation === "function") {
      e.stopPropagation();
    }
    if (isMaximized) {
      setIsMaximized(false);
    }
    if (isMinimized && typeof onClearUnread === "function") {
      onClearUnread();
    }
    if (typeof onToggleMinimize === "function") {
      onToggleMinimize();
    }
  };

  return (
    <div
      className={`linkedin-chat-dock ${isMinimized ? "is-minimized" : ""} ${isMaximized ? "is-maximized" : ""}`}
      role="region"
      aria-label="LinkedIn-style messaging widget"
    >
      {/* LinkedIn Dock Header Bar */}
      <div
        className="linkedin-chat-dock-header"
        onClick={handleToggleMinimize}
        title={isMinimized ? "Click to expand messages" : "Click to minimize messages"}
      >
        <div className="linkedin-chat-dock-title-group">
          <div className="linkedin-chat-dock-avatar-wrapper">
            <img
              src={userAvatar || dummyUser}
              alt=""
              className="linkedin-chat-dock-avatar"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = dummyUser;
              }}
            />
            <span className="linkedin-chat-dock-online-dot" />
          </div>

          <div className="linkedin-chat-dock-title-info">
            <div className="d-flex align-items-center gap-1">
              <span className="linkedin-chat-dock-title">Messaging</span>
              {unreadCount > 0 && (
                <span className="linkedin-chat-dock-badge">{unreadCount}</span>
              )}
            </div>
            {!isMinimized && userName && (
              <span className="linkedin-chat-dock-subtitle">
                {userName.length > 22 ? `${userName.slice(0, 22)}...` : userName}
              </span>
            )}
          </div>
        </div>

        {/* Action Controls on the Right (LinkedIn Style) */}
        <div
          className="linkedin-chat-dock-actions"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Minimize / Expand Toggle */}
          <button
            type="button"
            className="linkedin-chat-dock-btn"
            onClick={handleToggleMinimize}
            aria-label={isMinimized ? "Expand messages" : "Minimize messages"}
            title={isMinimized ? "Expand" : "Minimize"}
          >
            {isMinimized ? (
              <ChevronUpComp className="linkedin-chat-dock-icon" />
            ) : (
              <ChevronDownComp className="linkedin-chat-dock-icon" />
            )}
          </button>


          {/* Maximize Button on left side of Close button */}
          <button
            type="button"
            className="linkedin-chat-dock-btn maximize-btn"
            onClick={handleToggleMaximize}
            aria-label={isMaximized ? "Restore window size" : "Maximize window"}
            title={isMaximized ? "Restore" : "Maximize"}
          >
            {isMaximized ? (
              FullscreenExitIcon ? (
                <FullscreenExitIcon className="linkedin-chat-dock-icon" />
              ) : (
                <span className="linkedin-chat-dock-icon">❐</span>
              )
            ) : FullscreenIcon ? (
              <FullscreenIcon className="linkedin-chat-dock-icon" />
            ) : (
              <span className="linkedin-chat-dock-icon">□</span>
            )}
          </button>

          {/* Close Button */}
          <button
            type="button"
            className="linkedin-chat-dock-btn close-btn"
            onClick={onClose}
            aria-label="Close messaging"
            title="Close"
          >
            {CloseIcon ? (
              <CloseIcon className="linkedin-chat-dock-icon" />
            ) : (
              <svg
                className="linkedin-chat-dock-icon"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="currentColor"
              >
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Messaging Body Content */}
      <div className="linkedin-chat-dock-body">
        <MassengerModals
          setLgMsgShow={(open) => {
            if (!open && typeof onClose === "function") {
              onClose();
            }
          }}
          lgMsgShow={true}
          initialProfile={initialProfile}
          pageMode={true}
          isDockMinimized={isMinimized}
          isDockMaximized={isMaximized}
        />
      </div>
    </div>
  );
};

export default LinkedInChatDock;
